<template>
  <el-card>
    <div
        slot="header"
        class="clearfix">
      <span class="header-item">{{ location.name }} </span>
    </div>
    <div class="card-content">
      <el-row :gutter="20">
        <el-col :span="8">
          <div class="ring-card__title">
            Address
          </div>
          <p class="address-row location-description">
            <i class="el-icon-location" /> {{ locationAddress }}
          </p>
          <p class="address-row location-timezone">
            <i class="el-icon-time" /> {{ location.address.timezone || 'No information' }}
          </p>
          <div class="ring-card__title">
            Location Owner
          </div>
          <p class="address-row location-owner">
            {{ owner.email || 'No Owner' }}
          </p>
          <div class="ring-card__title">
            Location ID
          </div>
          <p class="address-row location-id">
            {{ locationId }}
          </p>
          <div
              v-if="showModeSchedules"
              data-test-id="mode-schedules-url"
              class="address-row">
            <a
                :href="location['mode_schedules_url']"
                target="_blank">View Mode Schedules</a>
          </div>
          <div
              v-if="sos.enabled"
              class="ring-card__title">
            <a
                :href="sos.ring_alarm_url"
                target="_blank"> SOS Enabled </a>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="ring-card__title">
            Users
          </div>
          <div
              v-for="user in ownerUsers"
              :key="user.user_id"
              class="users-row user"
              :class="{'selected': selectedSmbUser === null && user.user_id === selectedUser.user_id}">
            <div class="users-row-email">
              <span style="position: absolute; left: -30px; color: #999;">
                <font-awesome-icon icon="user" />
              </span>
              <a
                  class="user-email"
                  @click="selectUser(user)">
                {{ user.email }}
              </a>
              <small
                  v-if="user.user_id === location.owner_id"
                  style="color: #999; float: right">
                (Owner)
              </small>
              <div class="manage-user-link">
                <add-user-location
                    :user-id="user.user_id"
                    :location="location"
                    :users="ownerUsers"
                    :devices="devices"
                    @selected="selectUser(user)" />
              </div>
            </div>
          </div>

          <div 
              v-if="nonOwnerUsers.length > 0"
              class="ring-card__title non-owner-title">
            Other Users
          </div>
          <div
              v-for="user in nonOwnerUsers"
              :key="user.user_id"
              class="users-row user"
              :class="{'selected': user.user_id === selectedUser.user_id}">
            <div class="users-row-email">
              <span style="position: absolute; left: -30px; color: #999;">
                <font-awesome-icon icon="user" />
              </span>
              <a
                  class="user-email"
                  @click="selectUser(user)">
                {{ user.email }}
              </a>
              <small
                  style="color: #999; float: right">
                ({{ user.role }})
              </small>
              <div class="manage-user-link">
                <add-user-location
                    :user-id="user.user_id"
                    :location="location"
                    :users="users"
                    :devices="devices"
                    @selected="selectUser(user)" />
              </div>
            </div>
          </div>

          <div
              v-if="smbUsers.length > 0"
              class="ring-card__title non-owner-title">
            Authorized Users
          </div>
          <div
              v-for="(user, index) in smbUsers"
              :key="'smb-' + index + '-' + user.user_id"
              class="users-row user"
              :class="{'selected': selectedSmbUser === user}">
            <div class="users-row-email">
              <span style="position: absolute; left: -30px; color: #999;">
                <font-awesome-icon icon="user" />
              </span>
              <a
                  class="user-email"
                  @click="selectUser(user)">
                {{ user.email }}
              </a>
              <small
                  style="color: #999; float: right">
                ({{ getRBACRoleDescription(user.role_name) }})
              </small>
            </div>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="email">
            {{ selectedUser.email }}
          </div>
          <div class="ring-card__title">
            Devices
          </div>
          <div style="padding: 10px 0px">
            <div v-if="userSelectedDevices.length > 0">
              <div
                  v-for="(device, index) in userSelectedDevices"
                  :key="index"
                  class="devices-row">
                <device-location-row
                    :device="device"
                    :user-role="selectedUser.roleName" />
              </div>
            </div>
            <div v-else>
              <el-alert
                  title="No devices for this user"
                  type="info"
                  center
                  show-icon />
            </div>
          </div>
        </el-col>
      </el-row>
    </div>
  </el-card>
</template>

<script>
import AddUserLocation from "./AddUser";
import DeviceLocationRow from "./DeviceLocationRow";
import { get } from "lodash";
import { getRBACRoleDescription } from "@/utils/utils";

export default {
  name: "LocationDetail",
  components: {
    AddUserLocation,
    DeviceLocationRow,
  },
  props: {
    location: {
      required: true,
      type: Object,
    },
    users: {
      required: true,
      type: Array,
    },
    smbUsers: {
      required: false,
      type: Array,
      default: () => [],
    },
    devices: {
      required: true,
      type: Array,
    },
    owner: {
      required: true,
      type: Object,
      default: () => {
        return {};
      },
    },
    sos: {
      required: false,
      type: Object,
      default: () => {
        return {};
      },
    },
  },
  data() {
    return {
      selectedUser: { email: null, devices: [] },
      selectedSmbUser: null,
    };
  },
  computed: {
    showModeSchedules() {
      const url = String(this.location.mode_schedules_url);
      return !url.includes("***");
    },
    userSelectedDevices() {
      let userDevices = this.selectedUser.devices || [];
      let onlyChimesOwnByUser = (device) => {
        let isChime = /chime/.test(device.type);
        return isChime === false || (isChime && device.role === "owner");
      };
      let userDevs = userDevices.filter((d) => {
        return d.location_id === this.location.location_id;
      });
      return userDevs.filter(onlyChimesOwnByUser);
    },
    locationAddress() {
      const { address1, address2, city, state, zip_code, country } =
        this.location.address;
      return [address1, address2, city, state, zip_code, country]
        .filter((addressPart) => !!addressPart)
        .join(", ");
    },
    locationId() {
      return get(this.location, "location_id", "No Location ID");
    },
    ownerUsers() {
      return this.users.filter((user) => user.role === "owner");
    },
    nonOwnerUsers() {
      return this.users.filter((user) => user.role !== "owner");
    },
  },
  watch: {
    "owner.id": function () {
      this.setOwner();
    },
    ownerUsers: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.selectUser(newVal[0]);
        }
      },
      immediate: true,
    },
  },
  created() {
    this.setOwner();
  },
  mounted() {
    this.setOwner();
  },
  methods: {
    getRBACRoleDescription,
    setOwner() {
      this.selectedUser = { email: null, devices: [] };
      let owner =
        this.users.find((u) => this.location.owner_id === u.user_id) || {
          email: null,
          devices: [],
        } ||
        {};
      this.selectedUser = owner;
    },
    selectUser(user) {
      if (user.role_name && !user.devices) {
        // Find an owner user to get their devices
        const ownerUser = this.ownerUsers[0] || { devices: [] };
        // Create a new user object with the smbUser's properties plus the owner's devices
        this.selectedUser = {
          ...user,
          devices: ownerUser.devices || [],
          isSmbUser: true, // Add a flag to identify this as an smbUser
          roleName: user.role_name,
        };
        this.selectedSmbUser = user;
      } else {
        // Regular user selection
        this.selectedUser = {
          ...user,
          isSmbUser: false, // Add a flag to identify this as not an smbUser
        };
        this.selectedSmbUser = null;
      }
    },
  },
};
</script>

<style lang="scss">
.email {
  color: #999;
  font-size: 14px;
  float: right;
}

.manage-user-link {
  position: absolute;
  right: -30px;
  top: 0px;
}

.selected a.user-email {
  color: #f56c6c !important;
}

.devices-row {
  margin: 1em 0px;
  &:last-child {
    margin: 0px;
  }
}

.non-owner-title {
  margin-top: 20px;
  padding-top: 10px;
  border-top: 1px solid #ebeef5;
}
</style>
